import gql from 'graphql-tag';

import {
    address,
    cancellationPolicy,
    clientLabel,
    clientNote,
    clientOffice,
    clientOrder,
    creditBalance,
    fullOfficeClient,
    officeAllIntegrations,
    officeClient,
    officeConfig,
    officeEmployee,
    officeEntryCredit,
    officeEntryPass,
    officeLectureTemplate,
    officeMailchimpIntegration,
    officeNotificationConfig,
    officePaymentMethod,
    officePlace,
    officeTerms,
    officeVoucher,
    organisationLicensePayment,
    organisationLicensePaymentAddress,
    organisationOffice,
    passBalance,
    promoCodeOffice,
    userOrganisationClient,
} from '../fragments';

export const modifyOtherOfficeCurrency = gql`
    mutation ModifyOtherOfficeCurrency($input: ModifyOtherOfficeCurrencyInput!) {
        modifyOtherOfficeCurrency(input: $input) {
            ...OrganisationOfficeFragment
        }
    }
    ${organisationOffice}
`;

export const updateOfficeNotificationConfigByOfficeId = gql`
    mutation UpdateOfficeNotificationConfigByOfficeId($officeId: Int!, $input: OfficeNotificationConfigInput!) {
        updateOfficeNotificationConfigByOfficeId(officeId: $officeId, input: $input) {
            ...OfficeNotificationConfigFragment
        }
    }
    ${officeNotificationConfig}
`;

export const updateAddressById = gql`
    mutation UpdateAddressById($officeId: Int!, $addressId: Int!, $input: UpdateAddressInput!) {
        updateAddressById(officeId: $officeId, addressId: $addressId, input: $input) {
            ...AddressFragment
        }
    }
    ${address}
`;

export const updateBillingAddressById = gql`
    mutation UpdateBillingAddressById(
        $officeId: Int!
        $organisationId: Int!
        $licensePaymentAddressId: Int!
        $input: UpdateBillingAddressInput!
    ) {
        updateBillingAddressById(
            officeId: $officeId
            organisationId: $organisationId
            licensePaymentAddressId: $licensePaymentAddressId
            input: $input
        ) {
            ...OrganisationLicensePaymentAddressFragment
        }
    }
    ${organisationLicensePaymentAddress}
`;

export const updateOfficeById = gql`
    mutation UpdateOfficeById($officeId: Int!, $input: UpdateOfficeInput!) {
        updateOfficeById(officeId: $officeId, input: $input) {
            ...OrganisationOfficeFragment
            officeConfig {
                id
                officeConfigId
                colorSchemePrimaryColor
                timezone
                openingHours
                closingTime
                publicCalendarView
                hideDashboardStats
                alternatesAllowed
                closeLogoutBeforeAllowedWhenever
                closeLogoutBeforeHours
                penaltyZonesAllowed
                cashPaymentForReservationEnabled
                stripeConnectAccountId
                doNotShowAttendeesForLecturesAndCourses
                doNotShowAttendeesForCourses
                promoCodesVisibilityEnabled
                vouchersVisibilityEnabled
            }
        }
    }
    ${organisationOffice}
`;

/**
 * create or update
 */
export const createOfficeTerms = gql`
    mutation CreateOfficeTerms($input: CreateOfficeTermsInput!) {
        createOfficeTerms(input: $input) {
            ...OfficeTermsFragment
        }
    }
    ${officeTerms}
`;

export const createAddress = gql`
    mutation CreateAddress($officeId: Int!, $input: CreateAddressInput!) {
        createAddress(officeId: $officeId, input: $input) {
            ...AddressFragment
        }
    }
    ${address}
`;

export const createOrganisationLicensePayment = gql`
    mutation CreateOrganisationLicensePayment(
        $officeId: Int!
        $organisationId: Int!
        $bookTariffId: Int!
        $paymentType: String!
    ) {
        createOrganisationLicensePayment(
            officeId: $officeId
            organisationId: $organisationId
            bookTariffId: $bookTariffId
            paymentType: $paymentType
        ) {
            ...OrganisationLicensePaymentFragment
        }
    }
    ${organisationLicensePayment}
`;

export const updateStripeSubscriptionPayment = gql`
    mutation UpdateStripeSubscriptionPayment(
        $officeId: Int!
        $organisationId: Int!
        $newTariffToUpdate: String!
        $type: String!
    ) {
        updateStripeSubscriptionPayment(
            officeId: $officeId
            organisationId: $organisationId
            newTariffToUpdate: $newTariffToUpdate
            type: $type
        ) {
            success
        }
    }
`;

export const cancelScheduledStripeSubscription = gql`
    mutation CancelScheduledStripeSubscription($officeId: Int!, $organisationId: Int!) {
        cancelScheduledStripeSubscription(officeId: $officeId, organisationId: $organisationId) {
            success
        }
    }
`;

export const createOrganisationSmsPackagePayment = gql`
    mutation CreateOrganisationSmsPackagePayment($officeId: Int!, $organisationId: Int!, $smsTariff: String!) {
        createOrganisationSmsPackagePayment(
            officeId: $officeId
            organisationId: $organisationId
            smsTariff: $smsTariff
        ) {
            paymentIntentId
            clientSecret
            price
            currency
        }
    }
`;

export const updateOfficeLectureTemplateById = gql`
    mutation UpdateOfficeLectureTemplateById(
        $officeId: Int!
        $lectureTemplateId: Int!
        $input: UpdateOfficeLectureTemplateInput!
    ) {
        updateOfficeLectureTemplateById(officeId: $officeId, lectureTemplateId: $lectureTemplateId, input: $input) {
            ...OfficeLectureTemplateFragment
            place {
                ...OfficePlaceFragment
            }
            lecturer {
                ...OfficeEmployeeFragment
            }
        }
    }
    ${officeLectureTemplate}
    ${officePlace}
    ${officeEmployee}
`;

export const createOfficeLectureTemplate = gql`
    mutation CreateOfficeLectureTemplate($input: CreateOfficeLectureTemplateInput!) {
        createOfficeLectureTemplate(input: $input) {
            ...OfficeLectureTemplateFragment
            place {
                ...OfficePlaceFragment
            }
            lecturer {
                ...OfficeEmployeeFragment
            }
        }
    }
    ${officeLectureTemplate}
    ${officePlace}
    ${officeEmployee}
`;

export const updateBillingInfoForClientById = gql`
    mutation UpdateBillingInfoForClientById($officeId: Int!, $clientId: Int!, $input: UpdateClientBillingInfoInput!) {
        updateBillingInfoForClientById(officeId: $officeId, clientId: $clientId, input: $input) {
            ...FullOfficeClientFragment
        }
    }
    ${fullOfficeClient}
`;

export const updateClientById = gql`
    mutation UpdateClientById($officeId: Int!, $clientId: Int!, $input: UpdateClientInput!) {
        updateClientById(officeId: $officeId, clientId: $clientId, input: $input) {
            ...FullOfficeClientFragment
        }
    }
    ${fullOfficeClient}
`;

export const deleteOrganisationClientById = gql`
    mutation DeleteOrganisationClientById($officeId: Int!, $clientId: Int!) {
        deleteOrganisationClientById(officeId: $officeId, clientId: $clientId) {
            ...FullOfficeClientFragment
        }
    }
    ${fullOfficeClient}
`;

export const updateClientOfficeDataById = gql`
    mutation UpdateClientOfficeDataById($officeId: Int!, $clientId: Int!, $input: UpdateClientOfficeDataInput!) {
        updateClientOfficeDataById(officeId: $officeId, clientId: $clientId, input: $input) {
            ...FullOfficeClientFragment
        }
    }
    ${fullOfficeClient}
`;

export const deleteOfficeLectureTemplateById = gql`
    mutation DeleteOfficeLectureTemplateById($officeId: Int!, $lectureTemplateId: Int!) {
        deleteOfficeLectureTemplateById(officeId: $officeId, lectureTemplateId: $lectureTemplateId) {
            isDeleted
        }
    }
`;

export const deleteOfficeEmployeeById = gql`
    mutation DeleteOfficeEmployeeById($officeId: Int!, $employeeId: Int!, $transferEmployeeId: Int) {
        deleteOfficeEmployeeById(
            officeId: $officeId
            employeeId: $employeeId
            transferEmployeeId: $transferEmployeeId
        ) {
            success
        }
    }
`;

export const leaveMyOfficeEmploymentById = gql`
    mutation LeaveMyOfficeEmploymentById($officeId: Int!, $employeeId: Int!) {
        leaveMyOfficeEmploymentById(officeId: $officeId, employeeId: $employeeId) {
            success
        }
    }
`;

export const createClient = gql`
    mutation CreateClient($input: CreateClientInput!) {
        createClient(input: $input) {
            ...OfficeClientFragment
        }
    }
    ${officeClient}
`;

export const createOfficeMembership = gql`
    mutation CreateOfficeMembership($input: CreateOfficeMembershipInput!) {
        createOfficeMembership(input: $input) {
            client {
                ...OrganisationClientFragment
            }
            didClientExist
        }
    }
    ${userOrganisationClient}
`;

export const createSmartemailingIntegration = gql`
    mutation CreateSmartemailingIntegration($input: CreateSmartemailingIntegrationInput!) {
        createSmartemailingIntegration(input: $input) {
            ...OfficeAllIntegrationsFragment
        }
    }
    ${officeAllIntegrations}
`;

export const createMailerliteIntegration = gql`
    mutation CreateMailerliteIntegration($input: CreateMailerliteIntegrationInput!) {
        createMailerliteIntegration(input: $input) {
            ...OfficeAllIntegrationsFragment
        }
    }
    ${officeAllIntegrations}
`;

export const createEcomailIntegration = gql`
    mutation CreateEcomailIntegration($input: CreateEcomailIntegrationInput!) {
        createEcomailIntegration(input: $input) {
            ...OfficeAllIntegrationsFragment
        }
    }
    ${officeAllIntegrations}
`;

export const updateSmartemailingIntegration = gql`
    mutation UpdateSmartemailingIntegration($input: UpdateSmartemailingIntegrationInput!) {
        updateSmartemailingIntegration(input: $input) {
            ...OfficeAllIntegrationsFragment
        }
    }
    ${officeAllIntegrations}
`;

export const updateMailerliteIntegration = gql`
    mutation UpdateMailerliteIntegration($input: UpdateMailerliteIntegrationInput!) {
        updateMailerliteIntegration(input: $input) {
            ...OfficeAllIntegrationsFragment
        }
    }
    ${officeAllIntegrations}
`;

export const updateEcomailIntegration = gql`
    mutation UpdateEcomailIntegration($input: UpdateEcomailIntegrationInput!) {
        updateEcomailIntegration(input: $input) {
            ...OfficeAllIntegrationsFragment
        }
    }
    ${officeAllIntegrations}
`;

export const revokeMailchimpIntegration = gql`
    mutation RevokeMailchimpIntegration($input: RevokeMailchimpIntegrationInput!) {
        revokeMailchimpIntegration(input: $input) {
            ...OfficeMailchimpIntegrationFragment
        }
    }
    ${officeMailchimpIntegration}
`;

export const updateMailchimpIntegration = gql`
    mutation UpdateMailchimpIntegration($input: UpdateMailchimpIntegrationInput!) {
        updateMailchimpIntegration(input: $input) {
            ...OfficeAllIntegrationsFragment
        }
    }
    ${officeAllIntegrations}
`;

export const createClientNote = gql`
    mutation CreateClientNote($input: OfficeClientNoteInput!) {
        createClientNote(input: $input) {
            ...ClientNoteFragment
        }
    }
    ${clientNote}
`;

export const createClientLabel = gql`
    mutation CreateClientLabel($input: OfficeClientLabelInput!) {
        createClientLabel(input: $input) {
            ...ClientLabelFragment
        }
    }
    ${clientLabel}
`;

export const deleteClientLabel = gql`
    mutation DeleteClientLabel($officeId: Int!, $clientLabelId: Int!) {
        deleteClientLabel(officeId: $officeId, clientLabelId: $clientLabelId) {
            idFromCache
            success
        }
    }
`;

export const deleteOrgLabel = gql`
    mutation DeleteOrgLabel($officeId: Int!, $orgLabelId: Int!) {
        deleteOrgLabel(officeId: $officeId, orgLabelId: $orgLabelId) {
            idFromCache
            success
        }
    }
`;

export const createEmployee = gql`
    mutation CreateEmployee($input: OfficeEmployeeInput!) {
        createEmployee(input: $input) {
            ...OfficeEmployeeFragment
        }
    }
    ${officeEmployee}
`;

export const updateOfficeEmployeeById = gql`
    mutation UpdateOfficeEmployeeById($officeId: Int!, $employeeId: Int!, $input: UpdateOfficeEmployeeInput!) {
        updateOfficeEmployeeById(officeId: $officeId, employeeId: $employeeId, input: $input) {
            ...OfficeEmployeeFragment
        }
    }
    ${officeEmployee}
`;

export const createOfficeCancellationPolicyTemplate = gql`
    mutation CreateOfficeCancellationPolicyTemplate($input: CreateOfficeCancellationPolicyInput!) {
        createOfficeCancellationPolicyTemplate(input: $input) {
            ...CancellationPolicyFragment
        }
    }
    ${cancellationPolicy}
`;

export const updateOfficeCancellationPolicyTemplate = gql`
    mutation UpdateOfficeCancellationPolicyTemplate(
        $officeId: Int!
        $cancellationPolicyTemplateId: Int!
        $input: UpdateOfficeCancellationPolicyInput!
    ) {
        updateOfficeCancellationPolicyTemplate(
            officeId: $officeId
            cancellationPolicyTemplateId: $cancellationPolicyTemplateId
            input: $input
        ) {
            ...CancellationPolicyFragment
        }
    }
    ${cancellationPolicy}
`;

export const deleteOfficeCancellationPolicy = gql`
    mutation DeleteOfficeCancellationPolicy($officeId: Int!, $cancellationPolicyTemplateId: Int!) {
        deleteOfficeCancellationPolicy(
            officeId: $officeId
            cancellationPolicyTemplateId: $cancellationPolicyTemplateId
        ) {
            idFromCache
            success
        }
    }
`;

export const updateConfigByOfficeId = gql`
    mutation UpdateConfigByOfficeId($officeId: Int!, $input: UpdateOfficeConfigInput!) {
        updateConfigByOfficeId(officeId: $officeId, input: $input) {
            ...OfficeConfigFragment
        }
    }
    ${officeConfig}
`;

export const createOfficeEntryPass = gql`
    mutation CreateOfficeEntryPass($input: CreateOfficeEntryPassInput!) {
        createOfficeEntryPass(input: $input) {
            ...OfficeEntryPassFragment
        }
    }
    ${officeEntryPass}
`;

export const createOfficeEntryCredit = gql`
    mutation CreateOfficeEntryCredit($input: CreateOfficeEntryCreditInput!) {
        createOfficeEntryCredit(input: $input) {
            ...OfficeEntryCreditFragment
        }
    }
    ${officeEntryCredit}
`;

export const updateOfficeEntryPass = gql`
    mutation UpdateOfficeEntryPass($input: UpdateOfficeEntryPassInput!) {
        updateOfficeEntryPass(input: $input) {
            ...OfficeEntryPassFragment
        }
    }
    ${officeEntryPass}
`;

export const deleteOfficeEntryPass = gql`
    mutation DeleteOfficeEntryPass($officeId: Int!, $passEntryId: Int!, $active: Boolean!) {
        deleteOfficeEntryPass(officeId: $officeId, passEntryId: $passEntryId, active: $active) {
            ...OfficeEntryPassFragment
        }
    }
    ${officeEntryPass}
`;

export const updateOfficeEntryCredit = gql`
    mutation UpdateOfficeEntryCredit($input: UpdateOfficeEntryCreditInput!) {
        updateOfficeEntryCredit(input: $input) {
            ...OfficeEntryCreditFragment
        }
    }
    ${officeEntryCredit}
`;

export const deleteOfficeEntryCredit = gql`
    mutation DeleteOfficeEntryCredit($officeId: Int!, $creditEntryId: Int!, $active: Boolean!) {
        deleteOfficeEntryCredit(officeId: $officeId, creditEntryId: $creditEntryId, active: $active) {
            ...OfficeEntryCreditFragment
        }
    }
    ${officeEntryCredit}
`;

export const decreaseCreditEntryByOffice = gql`
    mutation DecreaseCreditEntryByOffice($officeId: Int!, $clientId: Int!, $newCount: Int!) {
        decreaseCreditEntryByOffice(officeId: $officeId, clientId: $clientId, newCount: $newCount) {
            ...CreditBalanceFragment
        }
    }
    ${creditBalance}
`;

export const decreasePassEntryByOffice = gql`
    mutation DecreasePassEntryByOffice($officeId: Int!, $clientId: Int!, $newCount: Int!) {
        decreasePassEntryByOffice(officeId: $officeId, clientId: $clientId, newCount: $newCount) {
            ...PassBalanceFragment
        }
    }
    ${passBalance}
`;

export const changeCreditEntryValidityByOffice = gql`
    mutation ChangeCreditEntryValidityByOffice($officeId: Int!, $clientId: Int!, $validTo: DateTime!) {
        changeCreditEntryValidityByOffice(officeId: $officeId, clientId: $clientId, validTo: $validTo) {
            ...CreditBalanceFragment
        }
    }
    ${creditBalance}
`;

export const changePassEntryValidityByOffice = gql`
    mutation ChangePassEntryValidityByOffice($officeId: Int!, $clientId: Int!, $validTo: DateTime!) {
        changePassEntryValidityByOffice(officeId: $officeId, clientId: $clientId, validTo: $validTo) {
            ...PassBalanceFragment
        }
    }
    ${passBalance}
`;

export const addCreditEntryByOffice = gql`
    mutation AddCreditEntryByOffice(
        $officeId: Int!
        $creditEntryId: Int
        $clientId: Int!
        $customEntryCount: CustomEntryCountInput
        $currency: String
        $price: Float
    ) {
        addCreditEntryByOffice(
            officeId: $officeId
            creditEntryId: $creditEntryId
            clientId: $clientId
            customEntryCount: $customEntryCount
            currency: $currency
            price: $price
        ) {
            creditBalance {
                ...CreditBalanceFragment
            }
        }
    }
    ${creditBalance}
`;

export const removeMakeUpSessionEntryByOffice = gql`
    mutation RemoveMakeUpSessionEntryByOffice($officeId: Int!, $alternateEntryId: Int!, $clientId: Int!) {
        removeMakeUpSessionEntryByOffice(
            officeId: $officeId
            alternateEntryId: $alternateEntryId
            clientId: $clientId
        ) {
            ...ClientOfficeFragment
        }
    }
    ${clientOffice}
`;

export const addMakeUpSessionEntryByOffice = gql`
    mutation AddMakeUpSessionEntryByOffice($officeId: Int!, $clientId: Int!, $makeUpSessionsValidForDays: Int!) {
        addMakeUpSessionEntryByOffice(
            officeId: $officeId
            clientId: $clientId
            makeUpSessionsValidForDays: $makeUpSessionsValidForDays
        ) {
            ...ClientOfficeFragment
        }
    }
    ${clientOffice}
`;

export const addPassEntryByOffice = gql`
    mutation AddPassEntryByOffice(
        $officeId: Int!
        $passEntryId: Int
        $clientId: Int!
        $customEntryCount: CustomEntryCountInput
        $currency: String
        $price: Float
    ) {
        addPassEntryByOffice(
            officeId: $officeId
            passEntryId: $passEntryId
            clientId: $clientId
            customEntryCount: $customEntryCount
            currency: $currency
            price: $price
        ) {
            passBalance {
                ...PassBalanceFragment
            }
        }
    }
    ${passBalance}
`;

export const updateClientOrderByOffice = gql`
    mutation UpdateClientOrderByOffice($officeId: Int!, $input: ClientOrderInput!) {
        updateClientOrderByOffice(officeId: $officeId, input: $input) {
            ...ClientOrderFragment
        }
    }
    ${clientOrder}
`;

export const updateOfficePaymentMethod = gql`
    mutation UpdateOfficePaymentMethod($officeId: Int!, $input: OfficePaymentMethodInput!) {
        updateOfficePaymentMethod(officeId: $officeId, input: $input) {
            office {
                ...OrganisationOfficeFragment
            }
            updatedOfficePaymentMethods {
                ...OfficePaymentMethodFragment
            }
        }
    }
    ${organisationOffice}
    ${officePaymentMethod}
`;

export const createOfficePaymentMethod = gql`
    mutation CreateOfficePaymentMethod($officeId: Int!, $input: BenefitsAndOtherInput!) {
        createOfficePaymentMethod(officeId: $officeId, input: $input) {
            ...OfficePaymentMethodFragment
        }
    }
    ${officePaymentMethod}
`;

export const deleteOfficePaymentMethodById = gql`
    mutation DeleteOfficePaymentMethodById($officeId: Int!, $officePaymentMethodId: Int!) {
        deleteOfficePaymentMethodById(officeId: $officeId, officePaymentMethodId: $officePaymentMethodId) {
            idFromCache
            success
        }
    }
`;

export const createPromoCode = gql`
    mutation CreatePromoCode($officeId: Int!, $input: CreatePromoCodeInput!) {
        createPromoCode(officeId: $officeId, input: $input) {
            ...PromoCodeOfficeFragment
        }
    }
    ${promoCodeOffice}
`;
export const updatePromoCode = gql`
    mutation UpdatePromoCode($officeId: Int!, $promoCodeId: Int!, $input: UpdatePromoCodeInput!) {
        updatePromoCode(officeId: $officeId, promoCodeId: $promoCodeId, input: $input) {
            ...PromoCodeOfficeFragment
        }
    }
    ${promoCodeOffice}
`;

export const deletePromoCodeById = gql`
    mutation DeletePromoCodeById($officeId: Int!, $promoCodeId: Int!) {
        deletePromoCodeById(officeId: $officeId, promoCodeId: $promoCodeId) {
            idFromCache
            success
        }
    }
`;

export const createVoucher = gql`
    mutation CreateVoucher($officeId: Int!, $input: CreateOfficeVoucherInput!) {
        createVoucher(officeId: $officeId, input: $input) {
            ...OfficeVoucherFragment
        }
    }
    ${officeVoucher}
`;

export const deleteVoucherById = gql`
    mutation DeleteVoucherById($officeId: Int!, $officeVoucherId: Int!) {
        deleteVoucherById(officeId: $officeId, officeVoucherId: $officeVoucherId) {
            idFromCache
            success
        }
    }
`;
